import { createContext, useContext } from 'react';
// these are only interfaces Which evaporate on transpilation.
import {
  DragSourceHookSpec,
  ConnectDragSource,
  ConnectDragPreview,
  DropTargetHookSpec,
  ConnectDropTarget,
  DragLayerMonitor,
  DragSourceMonitor,
  DropTargetMonitor,
  FactoryOrInstance,
} from 'react-dnd';

// Any more hooks required to be used in the commons code can be added to this interface
export interface ReactDndConstructs {
  useDrag<DragObject = any, DropResult = any, CollectedProps = any>(
    spec: FactoryOrInstance<DragSourceHookSpec<DragObject, DropResult, CollectedProps>>,
    deps?: any[]
  ): [CollectedProps, ConnectDragSource, ConnectDragPreview];

  useDrop<DragObject = any, DropResult = any, CollectedProps = any>(
    spec: FactoryOrInstance<DropTargetHookSpec<DragObject, DropResult, CollectedProps>>,
    deps?: any[]
  ): [CollectedProps, ConnectDropTarget];

  useDragLayer<CollectedProps, DragObject = any>(
    collect: (monitor: DragLayerMonitor<DragObject>) => CollectedProps
  ): CollectedProps;

  getEmptyImage: () => HTMLImageElement; // seems would be commonly used for pursuing custom drag image.
}

const defaultDragMonitor: DragSourceMonitor = {
  canDrag: () => false,
  isDragging: () => false,
  getItem: () => null as any,
  getItemType: () => null,
  getDropResult: () => null,
  didDrop: () => false,
  getInitialClientOffset: () => null,
  getInitialSourceClientOffset: () => null,
  getClientOffset: () => null,
  getSourceClientOffset: () => null,
  getDifferenceFromInitialOffset: () => null,
  getTargetIds: () => [],
  receiveHandlerId: () => {},
  getHandlerId: () => null,
  subscribeToStateChange: () => () => {},
};

const defaultDropMonitor: DropTargetMonitor = {
  canDrop: () => false,
  isOver: () => false,
  getItem: () => null as any,
  getItemType: () => null,
  getDropResult: () => null,
  didDrop: () => false,
  getInitialClientOffset: () => null,
  getInitialSourceClientOffset: () => null,
  getClientOffset: () => null,
  getSourceClientOffset: () => null,
  getDifferenceFromInitialOffset: () => null,
  receiveHandlerId: () => {},
  getHandlerId: () => null,
  subscribeToStateChange: () => () => {},
};

const defaultDragLayerMonitor: DragLayerMonitor = {
  isDragging: () => false,
  getItem: () => null as any,
  getItemType: () => null,
  getInitialClientOffset: () => null,
  getInitialSourceClientOffset: () => null,
  getClientOffset: () => null,
  getSourceClientOffset: () => null,
  getDifferenceFromInitialOffset: () => null,
};

export const DefaultReactDndConstructs: ReactDndConstructs = {
  useDrag: (spec) => [
    typeof spec === 'function'
      ? spec().collect?.(defaultDragMonitor) ?? ({} as any)
      : spec.collect?.(defaultDragMonitor) ?? ({} as any),
    () => null,
    () => null,
  ],
  useDrop: (spec) => [
    typeof spec === 'function'
      ? spec().collect?.(defaultDropMonitor) ?? ({} as any)
      : spec.collect?.(defaultDropMonitor) ?? ({} as any),
    () => null,
  ],
  useDragLayer: (spec) => spec(defaultDragLayerMonitor),
  getEmptyImage: () => new Image(),
};

export const ReactDndCtxt = createContext<ReactDndConstructs>(DefaultReactDndConstructs);

export function useReactDndConstructs() {
  return useContext(ReactDndCtxt);
}
